import { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, Circle, Check, Loader2, ChevronUp, type LucideIcon } from 'lucide-react';
import { useSocialNetworkConnect } from '@/hooks/useSocialNetworkConnect';
import type { Channel } from '@/services/business/calendar';

interface TaskItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  deadline?: Date;
}

interface TaskCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  count: number;
  onClick: () => void;
  nextDeadline?: Date;
  items?: TaskItem[];
  expandable?: boolean;
  isNetworkCategory?: boolean; // Special handling for network connections
}

interface TaskDigestProps {
  readonly categories: TaskCategory[];
  readonly onNetworkConnected?: () => void;
  readonly initialExpandedCategories?: string[];
  readonly variant?: 'card' | 'inline';
  readonly onShowNetworkPanel?: () => void; // Callback to show network panel on right side (desktop)
  readonly onTaskClicked?: () => void; // Callback when any task is clicked (to close other panels)
  readonly isCollapsed?: boolean; // External control for collapsed state
  readonly onToggleCollapse?: () => void; // Callback when user toggles collapse
}

const getDeadlineText = (deadline: Date): string => {
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffHours < 0) {
    return 'En retard';
  } else if (diffHours === 0) {
    return 'Maintenant';
  } else if (diffHours < 24) {
    return `H-${diffHours}`;
  } else if (diffDays === 1) {
    return 'Demain';
  } else {
    return `J-${diffDays}`;
  }
};

const getDeadlineColor = (deadline: Date): string => {
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  
  if (diffDays < 2) {
    return 'text-red-400/80';
  } else if (diffDays < 7) {
    return 'text-amber-400/70';
  }
  return 'text-white/40';
};

// Animation timing constants (in ms)
const ANIMATION_CHECK_DURATION = 1200;  // Time to show the check mark
const ANIMATION_EXIT_DURATION = 600;    // Time for fade out / collapse
const ANIMATION_TOTAL_DURATION = ANIMATION_CHECK_DURATION + ANIMATION_EXIT_DURATION;

export function TaskDigest({ categories, onNetworkConnected, initialExpandedCategories, variant = 'card', onShowNetworkPanel, onTaskClicked, isCollapsed = false, onToggleCollapse }: TaskDigestProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(initialExpandedCategories || [])
  );
  
  // Close expanded categories when collapsed externally
  useEffect(() => {
    if (isCollapsed) {
      setExpandedCategories(new Set());
    }
  }, [isCollapsed]);
  const [connectingNetworks, setConnectingNetworks] = useState<Set<string>>(new Set());
  const [connectedNetworks, setConnectedNetworks] = useState<Set<string>>(new Set());
  const { connectNetwork } = useSocialNetworkConnect();
  
  // Animation states for categories
  const [visibleCategories, setVisibleCategories] = useState<TaskCategory[]>([]);
  const [completingCategoryIds, setCompletingCategoryIds] = useState<Set<string>>(new Set());
  const [exitingCategoryIds, setExitingCategoryIds] = useState<Set<string>>(new Set());
  const prevCategoriesRef = useRef<TaskCategory[]>([]);
  
  // Animation states for items (videos, etc.)
  const [completingItemIds, setCompletingItemIds] = useState<Set<string>>(new Set());
  const [exitingItemIds, setExitingItemIds] = useState<Set<string>>(new Set());
  const prevItemsRef = useRef<Map<string, Set<string>>>(new Map());
  const removedItemsRef = useRef<Map<string, { item: TaskItem; categoryId: string }>>(new Map());
  
  // Track network categories that are fully connected locally (before parent refresh)
  const [locallyCompletedNetworkCategories, setLocallyCompletedNetworkCategories] = useState<Set<string>>(new Set());

  // Helper function to store a removed item for animation
  const storeRemovedItem = (itemId: string, categoryId: string) => {
    const prevCategory = prevCategoriesRef.current.find(c => c.id === categoryId);
    const removedItem = prevCategory?.items?.find(item => item.id === itemId);
    if (removedItem) {
      removedItemsRef.current.set(itemId, { item: removedItem, categoryId });
    }
  };

  // Helper function to process a single removed item
  const processRemovedItem = (
    itemId: string,
    currentItemIds: Set<string>,
    categoryId: string,
    newCompletingItems: Set<string>
  ) => {
    if (!currentItemIds.has(itemId)) {
      newCompletingItems.add(itemId);
      storeRemovedItem(itemId, categoryId);
    }
  };

  // Helper function to detect removed items in a category
  const detectRemovedItemsInCategory = (
    category: TaskCategory,
    newCompletingItems: Set<string>
  ) => {
    if (!category.items || category.isNetworkCategory) {
      return;
    }

    const currentItemIds = new Set(category.items.map(item => item.id));
    const prevItemIds = prevItemsRef.current.get(category.id);
    
    if (prevItemIds && prevItemIds.size > 0) {
      prevItemIds.forEach(itemId => {
        processRemovedItem(itemId, currentItemIds, category.id, newCompletingItems);
      });
    }
    
    prevItemsRef.current.set(category.id, currentItemIds);
  };

  // Helper function to clean up removed categories
  const cleanupRemovedCategories = (categories: TaskCategory[]) => {
    const currentCategoryIds = new Set(categories.map(c => c.id));
    prevItemsRef.current.forEach((_, categoryId) => {
      if (!currentCategoryIds.has(categoryId)) {
        prevItemsRef.current.delete(categoryId);
      }
    });
  };

  // Helper function to auto-expand categories with completing items
  const expandCategoriesWithCompletingItems = (
    categories: TaskCategory[],
    newCompletingItems: Set<string>
  ) => {
    categories.forEach(category => {
      if (!category.items) {
        return;
      }
      const hasCompletingItem = category.items.some(item => newCompletingItems.has(item.id)) ||
        (prevItemsRef.current.get(category.id) && 
         [...(prevItemsRef.current.get(category.id) || [])].some(id => newCompletingItems.has(id)));
      if (hasCompletingItem) {
        setExpandedCategories(prev => new Set([...prev, category.id]));
      }
    });
  };

  // Helper function to schedule item animation cleanup
  const scheduleItemAnimationCleanup = (newCompletingItems: Set<string>) => {
    setTimeout(() => {
      setCompletingItemIds(prev => {
        const next = new Set(prev);
        newCompletingItems.forEach(id => next.delete(id));
        return next;
      });
      setExitingItemIds(prev => {
        const next = new Set(prev);
        newCompletingItems.forEach(id => next.delete(id));
        return next;
      });
      newCompletingItems.forEach(id => {
        removedItemsRef.current.delete(id);
      });
    }, ANIMATION_TOTAL_DURATION);
  };

  // Detect when items are removed from categories and animate them out
  useEffect(() => {
    const newCompletingItems = new Set<string>();
    
    categories.forEach(category => {
      detectRemovedItemsInCategory(category, newCompletingItems);
    });
    
    cleanupRemovedCategories(categories);
    
    if (newCompletingItems.size > 0) {
      expandCategoriesWithCompletingItems(categories, newCompletingItems);
      
      setCompletingItemIds(prev => new Set([...prev, ...newCompletingItems]));
      
      setTimeout(() => {
        setExitingItemIds(prev => new Set([...prev, ...newCompletingItems]));
      }, ANIMATION_CHECK_DURATION);
      
      scheduleItemAnimationCleanup(newCompletingItems);
    }
  }, [categories]);

  // Detect when categories are removed and animate them out
  useEffect(() => {
    const prevCategoryIds = new Set(prevCategoriesRef.current.map(c => c.id));
    const currentCategoryIds = new Set(categories.map(c => c.id));
    
    const removedCategoryIds = [...prevCategoryIds].filter(id => !currentCategoryIds.has(id));
    
    if (removedCategoryIds.length > 0 && visibleCategories.length > 0) {
      setCompletingCategoryIds(new Set(removedCategoryIds));
      
      setTimeout(() => {
        setExitingCategoryIds(new Set(removedCategoryIds));
      }, ANIMATION_CHECK_DURATION);
      
      setTimeout(() => {
        setVisibleCategories(categories);
        setCompletingCategoryIds(new Set());
        setExitingCategoryIds(new Set());
      }, ANIMATION_TOTAL_DURATION);
    } else {
      setVisibleCategories(categories);
    }
    
    prevCategoriesRef.current = categories;
  }, [categories]);

  // Initial render
  useEffect(() => {
    if (visibleCategories.length === 0 && categories.length > 0) {
      setVisibleCategories(categories);
    }
  }, [categories, visibleCategories.length]);

  // Calculate which categories will actually be displayed
  const displayableCategories = visibleCategories.filter(category => {
    const isNetworkCategory = category.isNetworkCategory;
    const isCompleting = completingCategoryIds.has(category.id);
    const isExiting = exitingCategoryIds.has(category.id);
    
    if (isNetworkCategory) {
      const displayItems = category.items?.filter(item => !connectedNetworks.has(item.id)) || [];
      const hasRemainingItems = displayItems.length > 0;
      return hasRemainingItems || isCompleting || isExiting;
    }
    return true;
  });

  if (displayableCategories.length === 0) return null;

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories(prev => {
      if (prev.has(categoryId)) {
        // Close if already open
        return new Set();
      } else {
        // Open this one and close others
        return new Set([categoryId]);
      }
    });
  };

  const triggerCategoryCompletion = (categoryId: string) => {
    setLocallyCompletedNetworkCategories(prev => new Set([...prev, categoryId]));
    setCompletingCategoryIds(prev => new Set([...prev, categoryId]));
    
    setTimeout(() => {
      setExitingCategoryIds(prev => new Set([...prev, categoryId]));
    }, ANIMATION_CHECK_DURATION);
    
    setTimeout(() => {
      setCompletingCategoryIds(prev => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
      setExitingCategoryIds(prev => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }, ANIMATION_TOTAL_DURATION);
  };

  const handleNetworkConnect = async (network: string, category: TaskCategory) => {
    setConnectingNetworks(prev => new Set([...prev, network]));
    
    const success = await connectNetwork(network as Channel);
    
    setConnectingNetworks(prev => {
      const next = new Set(prev);
      next.delete(network);
      return next;
    });
    
    if (success) {
      const newConnectedNetworks = new Set([...connectedNetworks, network]);
      setConnectedNetworks(newConnectedNetworks);
      
      // Check if all networks in this category are now connected
      if (category.items) {
        const allConnected = category.items.every(item => newConnectedNetworks.has(item.id));
        if (allConnected) {
          triggerCategoryCompletion(category.id);
        }
      }
      
      onNetworkConnected?.();
    }
  };

  // Handler for category button click
  const handleCategoryClick = (
    category: TaskCategory,
    hasItems: boolean,
    isCompleting: boolean
  ) => {
    if (isCompleting) return;
    onTaskClicked?.(); // Close other panels first
    if (category.isNetworkCategory && onShowNetworkPanel) {
      // On desktop, show network panel on the right side
      onShowNetworkPanel();
    } else if (hasItems) {
      toggleExpand(category.id);
    } else {
      category.onClick();
    }
  };

  // Helper function to render a single network item
  const renderNetworkItem = (item: TaskItem, category: TaskCategory, itemIndex: number, totalItems: number) => {
    const ItemIcon = item.icon;
    const isConnecting = connectingNetworks.has(item.id);
    const isNetworkConnected = connectedNetworks.has(item.id);
    
    if (isNetworkConnected) return null;
    
    return (
      <button
        key={item.id}
        onClick={() => {
          onTaskClicked?.();
          handleNetworkConnect(item.id, category);
        }}
        disabled={isConnecting}
        className={`w-full flex items-center gap-3.5 px-4 py-3 text-left transition-all duration-500 hover:bg-white/[0.04] active:bg-white/[0.06] ${
          itemIndex === totalItems - 1 ? '' : 'border-b border-white/[0.02]'
        }`}
      >
        {isConnecting ? (
          <Loader2 className="h-3.5 w-3.5 text-warning animate-spin shrink-0" />
        ) : (
          <Circle className="h-3.5 w-3.5 text-white/30 shrink-0" strokeWidth={2} />
        )}
        <div className="flex-1 flex items-center gap-2">
          {ItemIcon && <ItemIcon className="h-4 w-4 text-white/50" />}
          <span className="text-sm text-white/70">{item.label}</span>
        </div>
        {!isConnecting && (
          <ChevronRight className="h-4 w-4 text-white/20 shrink-0" />
        )}
      </button>
    );
  };

  // Helper function to render network connection items
  const renderNetworkItems = (category: TaskCategory) => {
    const networkItems = (category.items || []).filter(item => !connectedNetworks.has(item.id));
    return networkItems.map((item, itemIndex) => 
      renderNetworkItem(item, category, itemIndex, networkItems.length)
    );
  };

  // Helper function to render regular items with animation
  const renderRegularItems = (category: TaskCategory) => {
    const currentCategory = categories.find(c => c.id === category.id);
    const currentItems = currentCategory?.items || [];
    
    const animatingOutItems = Array.from(removedItemsRef.current.values())
      .filter(entry => entry.categoryId === category.id &&
        (completingItemIds.has(entry.item.id) || exitingItemIds.has(entry.item.id)))
      .map(entry => entry.item);
    
    const allItems = [
      ...currentItems,
      ...animatingOutItems.filter(item => !currentItems.some(ci => ci.id === item.id)),
    ];
    
    return allItems.map((item, itemIndex) => {
      const isItemCompleting = completingItemIds.has(item.id);
      const isItemExiting = exitingItemIds.has(item.id);
      
      return (
        <div
          key={item.id}
          className={`transition-all duration-500 ${
            isItemExiting ? 'opacity-0 max-h-0 overflow-hidden' : 'max-h-20'
          }`}
        >
          <button
            onClick={() => {
              onTaskClicked?.();
              item.onClick();
            }}
            disabled={isItemCompleting}
            className={`w-full flex items-center gap-3.5 px-4 py-3 text-left transition-all duration-500 ${
              isItemCompleting 
                ? 'bg-warning/10' 
                : 'hover:bg-white/[0.04] active:bg-white/[0.06]'
            } ${
              itemIndex !== allItems.length - 1 && !isItemExiting ? 'border-b border-white/[0.02]' : ''
            }`}
          >
            {isItemCompleting ? (
              <div className="h-3.5 w-3.5 rounded-full bg-warning flex items-center justify-center shrink-0 animate-scale-in">
                <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
              </div>
            ) : (
              <Circle className="h-3.5 w-3.5 text-white/30 shrink-0" strokeWidth={2} />
            )}
            <div className="flex-1 flex flex-col gap-0.5">
              <span className={`text-sm transition-all duration-500 ${
                  isItemCompleting ? 'text-warning line-through' : 'text-white/70'
                }`}>{item.label}</span>
              {!isItemCompleting && item.deadline && (
                <span className={`text-xs ${getDeadlineColor(item.deadline)}`}>
                  {getDeadlineText(item.deadline)}
                </span>
              )}
            </div>
            {!isItemCompleting && (
              <ChevronRight className="h-4 w-4 text-white/20 shrink-0" />
            )}
          </button>
        </div>
      );
    });
  };

  // Helper function to compute category display properties
  interface CategoryDisplayProps {
    isExpanded: boolean;
    hasItems: boolean;
    isLastCategory: boolean;
    isNetworkCategory: boolean;
    isCompleting: boolean;
    isExiting: boolean;
    displayItems: TaskItem[] | undefined;
    hasRemainingItems: boolean;
  }

  const computeCategoryDisplayProps = (
    category: TaskCategory,
    index: number,
    totalCategories: number
  ): CategoryDisplayProps => {
    const isExpanded = expandedCategories.has(category.id);
    const hasItems = category.expandable && category.items && category.items.length > 0;
    const isLastCategory = index === totalCategories - 1;
    const isNetworkCategory = category.isNetworkCategory ?? false;
    const isCompleting = completingCategoryIds.has(category.id);
    const isExiting = exitingCategoryIds.has(category.id);
    
    const displayItems = isNetworkCategory && category.items
      ? category.items.filter(item => !connectedNetworks.has(item.id))
      : category.items;
    
    const hasRemainingItems = displayItems && displayItems.length > 0;
    
    return {
      isExpanded,
      hasItems,
      isLastCategory,
      isNetworkCategory,
      isCompleting,
      isExiting,
      displayItems,
      hasRemainingItems,
    };
  };

  // Helper function to determine if category should be hidden
  const shouldHideCategory = (props: CategoryDisplayProps): boolean => {
    return props.isNetworkCategory && !props.hasRemainingItems && !props.isCompleting && !props.isExiting;
  };

  // Helper function to get category count display
  const getCategoryCount = (category: TaskCategory, props: CategoryDisplayProps): number => {
    if (props.isNetworkCategory) {
      return props.displayItems?.length || 0;
    }
    return category.count;
  };

  // Helper function to render category icon
  const renderCategoryIcon = (isCompleting: boolean) => {
    if (isCompleting) {
      return (
        <div className="h-4 w-4 rounded-full bg-warning flex items-center justify-center shrink-0 animate-scale-in">
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        </div>
      );
    }
    return <Circle className="h-4 w-4 text-warning shrink-0" strokeWidth={2} />;
  };

  // Helper function to render category chevron
  const renderCategoryChevron = (hasItems: boolean, isExpanded: boolean, isCompleting: boolean) => {
    if (isCompleting) return null;
    
    if (hasItems) {
      return (
        <ChevronDown className={`h-4 w-4 text-white/30 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      );
    }
    return <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />;
  };

  // Helper function to render a single category
  const renderCategory = (
    category: TaskCategory,
    props: CategoryDisplayProps
  ) => {
    const categoryCount = getCategoryCount(category, props);
    const showCount = !props.isCompleting && categoryCount > 1;

    return (
      <div 
        key={category.id}
        className={`transition-all duration-500 ${
          props.isExiting ? 'opacity-0 max-h-0 overflow-hidden' : 'max-h-96'
        }`}
      >
        <button
          onClick={() => handleCategoryClick(category, props.hasItems, props.isCompleting)}
          disabled={props.isCompleting}
          className={`w-full flex items-center gap-3.5 px-4 py-3.5 text-left transition-all duration-500 ${
            props.isCompleting 
              ? 'bg-warning/10' 
              : 'hover:bg-white/[0.04] active:bg-white/[0.06]'
          } ${
            !props.isLastCategory && !props.isExpanded ? 'border-b border-white/[0.04]' : ''
          }`}
        >
          {renderCategoryIcon(props.isCompleting)}
          <div className="flex-1 flex flex-col gap-0.5">
            <span className={`text-sm tracking-wide transition-all duration-500 ${
              props.isCompleting ? 'text-warning line-through' : 'text-white'
            }`}>
              {category.label}
              {showCount && (
                <span className="text-white/40 ml-1">({categoryCount})</span>
              )}
            </span>
            {!props.isCompleting && category.nextDeadline && (
              <span className={`text-xs ${getDeadlineColor(category.nextDeadline)}`}>
                {getDeadlineText(category.nextDeadline)}
              </span>
            )}
          </div>
          {renderCategoryChevron(props.hasItems, props.isExpanded, props.isCompleting)}
        </button>
        
        {props.hasRemainingItems && props.isExpanded && (
          <div className={`ml-4 border-l-2 border-warning/30 bg-black/20 ${!props.isLastCategory ? 'border-b border-white/[0.04]' : ''}`}>
            {props.isNetworkCategory ? renderNetworkItems(category) : renderRegularItems(category)}
          </div>
        )}
      </div>
    );
  };

  // Sort by most urgent deadline first
  const sortedCategories = [...visibleCategories].sort((a, b) => {
    if (!a.nextDeadline && !b.nextDeadline) return 0;
    if (!a.nextDeadline) return 1;
    if (!b.nextDeadline) return -1;
    return a.nextDeadline.getTime() - b.nextDeadline.getTime();
  });

  const isInline = variant === 'inline';
  
  // Show collapsed summary when multiple groups and collapsed
  const shouldShowCollapsedSummary = isCollapsed && displayableCategories.length > 1;
  const totalTaskCount = displayableCategories.reduce((sum, cat) => {
    if (cat.isNetworkCategory) {
      const displayItems = cat.items?.filter(item => !connectedNetworks.has(item.id)) || [];
      return sum + displayItems.length;
    }
    return sum + cat.count;
  }, 0);
  
  // Find the most urgent deadline across all categories
  const mostUrgentDeadline = displayableCategories.reduce((urgent: Date | undefined, cat) => {
    if (!cat.nextDeadline) return urgent;
    if (!urgent) return cat.nextDeadline;
    return cat.nextDeadline < urgent ? cat.nextDeadline : urgent;
  }, undefined);

  return (
    <div className={isInline ? '' : 'mb-6'}>
      <div className={isInline ? '' : 'bg-warning/[0.06] border border-warning/10 rounded-lg overflow-hidden'}>
        {/* Header - clickable when collapsed */}
        <button
          onClick={() => {
            if (displayableCategories.length > 1 && onToggleCollapse) {
              onToggleCollapse();
            }
          }}
          disabled={displayableCategories.length <= 1}
          className={`w-full px-4 py-2.5 flex items-center justify-between ${isInline ? '' : 'border-b border-white/[0.04]'} ${
            displayableCategories.length > 1 ? 'cursor-pointer hover:bg-white/[0.02] active:bg-white/[0.04] transition-colors' : 'cursor-default'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-white/40">
              Mes Tâches
            </span>
            {shouldShowCollapsedSummary && (
              <span className="text-xs text-white/60">
                · {totalTaskCount} en attente
                {mostUrgentDeadline && (
                  <span className={`ml-1 ${getDeadlineColor(mostUrgentDeadline)}`}>
                    · {getDeadlineText(mostUrgentDeadline)}
                  </span>
                )}
              </span>
            )}
          </div>
          {displayableCategories.length > 1 && (
            <ChevronUp className={`h-4 w-4 text-white/30 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`} />
          )}
        </button>
        
        {/* Categories list - animated collapse */}
        <div className={`transition-all duration-300 ease-out overflow-hidden ${
          shouldShowCollapsedSummary ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        }`}>
          {sortedCategories.map((category, index) => {
            const props = computeCategoryDisplayProps(category, index, sortedCategories.length);
          
            if (shouldHideCategory(props)) {
              return null;
            }
          
            return renderCategory(category, props);
          })}
        </div>
      </div>
    </div>
  );
}

export type { TaskCategory, TaskItem };
